import React, {useState} from 'react';

import {RequiredMark} from 'src/components/required-mark';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights/constants';
import {useGetCurrentEnvironment} from 'src/modules/manage-entitlements/all-entitlements/roles-grants-resources/hooks/useGetCurrentEnvironment';

import {Group, Item} from '@pb/kit-component-group';
import {Info} from '@pb/kit-component-info';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';
import TreeList, {TreeListBranches} from '@pb/kit-component-tree-list';

import {PermissionTree} from '../permission-tree';
import {PERMISSIONS_TREE_DATA} from '../permission-tree/constants';
import {getTreeCodes} from '../permission-tree/utils';

import type {PermissionTree as PermissionTreeType} from '../permission-tree/typedef';


type Props = {
    permissions: string[],
    defaultPermissions?: string[],
    setPermissions?: (permissions: string[]) => void,
    disabled?: boolean,
};

export const PermissionsTable = ({permissions, defaultPermissions, setPermissions, disabled}: Props) => {
    const [treeData, setTreeData] = useState(PERMISSIONS_TREE_DATA);
    const {environmentValue} = useGetCurrentEnvironment();

    return (
        <TreeListBranches tree={treeData}>
            {({setNode}) => (
                <Table>
                    <Head>
                        <Cell width={380}>Admin Right Name</Cell>
                        <Cell>
                            <Group gap="none" justify="space-around">
                                <Item>Delete</Item>
                                <Item>Create</Item>
                                <Item className="pdx-m">Edit</Item>
                                <Item>View</Item>
                                <Item>Restricted</Item>
                            </Group>
                        </Cell>
                    </Head>
                    <Body>
                        {treeData.map((data, index) => (
                            <TreeList<PermissionTreeType>
                                key={data.id}
                                data={data}
                                chain={`${index}`}
                                setNode={setNode}
                                renderRow={(rowData, Component) => {
                                    const {tree, module, explanation} = rowData.data;
                                    if (
                                        environmentValue === 'production' &&
                                        tree &&
                                        getTreeCodes(tree).includes(ACCESS_RIGHTS.IMPORT_ENTITLEMENTS)
                                    ) {
                                        return null;
                                    }

                                    const isCustomPermission = defaultPermissions && permissions && tree &&
                                        getTreeCodes(tree).some(code =>
                                            permissions.includes(code) !== defaultPermissions.includes(code),
                                        );

                                    const moduleView = explanation ? (
                                        <Info type={{popover: explanation}}>
                                            {module}
                                        </Info>
                                    ) : module;

                                    return (
                                        <Row key={rowData.id}>
                                            <Cell>
                                                <Component>
                                                    {isCustomPermission ? (
                                                        <RequiredMark label={moduleView}/>
                                                    ) : moduleView}
                                                </Component>
                                            </Cell>
                                            <Cell>
                                                {permissions && tree ? (
                                                    <PermissionTree
                                                        moduleName={module}
                                                        disabled={disabled}
                                                        codes={permissions}
                                                        tree={tree}
                                                        onTreeChange={setPermissions}
                                                    />
                                                ) : null}
                                            </Cell>
                                        </Row>
                                    );
                                }}
                                onExpand={expandData => {
                                    setTreeData(TreeList.expandOnlyTreeNodes(treeData, expandData, data.id));
                                }}
                            />
                        ))}
                    </Body>
                </Table>
            )}
        </TreeListBranches>
    );
};
